/**
 * @fileoverview Scratch extension for hand pose detection using TensorFlow.js HandPose model.
 * This extension allows sprites to interact with detected hand poses and finger positions through webcam input.
 */

const BlockType = require("../../extension-support/block-type");
const ArgumentType = require("../../extension-support/argument-type");
const formatMessage = require("format-message");
const Cast = require("../../util/cast");

/**
 * Icon svg to be displayed at left edge of each extension block, encoded as a data URI.
 * @type {string}
 */
const blockIconURI =
    "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHJ4PSI2IiBmaWxsPSIjRjE1QTgzIi8+PC9zdmc+";

/**
 * Configuration constants for the hand pose detection system.
 * @readonly
 */
const CONFIG = {
    // Video frame dimensions for hand detection
    VIDEO_WIDTH: 480,
    VIDEO_HEIGHT: 360,

    // Detection loop timing (in milliseconds)
    DETECTION_INTERVAL: 100,

    // Default video settings
    DEFAULT_VIDEO_TRANSPARENCY: 50,

    // CDN URLs for dynamic script loading
    TENSORFLOW_CDN_URL: "https://cdn.jsdelivr.net/npm/@tensorflow/tfjs",
    HANDPOSE_CDN_URL:
        "https://cdn.jsdelivr.net/npm/@tensorflow-models/handpose",
};

/**
 * Enum for video state values.
 * @readonly
 * @enum {string}
 */
const VideoState = {
    /** Video is turned off */
    OFF: "off",
    /** Video is on with normal orientation */
    ON: "on",
    /** Video is on with horizontally flipped display */
    ON_FLIPPED: "on-flipped",
};

/**
 * List of supported finger parts for hand pose detection.
 * Maps display names to handpose annotation keys.
 * @readonly
 */
const FINGER_PARTS = [
    { text: "thumb", value: "thumb" },
    { text: "index finger", value: "indexFinger" },
    { text: "middle finger", value: "middleFinger" },
    { text: "ring finger", value: "ringFinger" },
    { text: "pinky", value: "pinky" },
];

/**
 * List of finger sub-parts (joints) for detailed hand tracking.
 * Maps display names to joint indices.
 * @readonly
 */
const FINGER_SUB_PARTS = [
    { text: "base", value: 0 },
    { text: "first knuckle", value: 1 },
    { text: "second knuckle", value: 2 },
    { text: "tip", value: 3 },
];

/**
 * Scratch extension class for hand pose detection using TensorFlow.js HandPose.
 * Provides blocks for sprite movement based on detected hand poses and finger positions.
 */
class Scratch3PoseHandBlocks {
    /**
     * Create a new PoseHand extension instance.
     * @param {Runtime} runtime - The Scratch runtime instance
     */
    constructor(runtime) {
        /**
         * The Scratch runtime instance
         * @type {Runtime}
         * @private
         */
        this.runtime = runtime;

        /**
         * Current video display state
         * @type {string}
         * @private
         */
        this.globalVideoState = VideoState.ON;

        /**
         * Current video transparency percentage (0-100)
         * @type {number}
         * @private
         */
        this.globalVideoTransparency = CONFIG.DEFAULT_VIDEO_TRANSPARENCY;

        /**
         * Loaded HandPose model instance
         * @type {object|null}
         * @private
         */
        this.handModel = null;

        /**
         * Most recent hand pose detection result
         * @type {object|null}
         * @private
         */
        this.handPoseState = null;

        /**
         * Video frame dimensions for hand detection
         * @type {number[]}
         * @private
         */
        this.DIMENSIONS = [CONFIG.VIDEO_WIDTH, CONFIG.VIDEO_HEIGHT];

        /**
         * Supported finger parts configuration
         * @type {Array}
         * @private
         */
        this.fingerParts = FINGER_PARTS;

        /**
         * Supported finger sub-parts configuration
         * @type {Array}
         * @private
         */
        this.fingerSubParts = FINGER_SUB_PARTS;

        // Start hand detection if video devices are available
        if (this.runtime.ioDevices) {
            this._loop();
        }

        // Apply default extension settings
        this.projectStarted();
    }

    /**
     * Get extension metadata including blocks, menus, and display information.
     * @returns {object} Extension metadata object
     */
    getInfo() {
        return {
            id: "posehand",
            name: formatMessage({
                id: "posehand.categoryName",
                default: "Hand Sensing",
                description:
                    "Label for the hand pose detection extension category",
            }),
            blockIconURI: blockIconURI,
            blocks: [
                {
                    opcode: "goToHandPart",
                    text: formatMessage({
                        id: "posehand.goToHandPart",
                        default: "go to [HAND_PART] [HAND_SUB_PART]",
                        description:
                            "Move sprite to the detected position of a specific finger part",
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        HAND_PART: {
                            type: ArgumentType.STRING,
                            menu: "HAND_PART",
                            defaultValue: "thumb",
                        },
                        HAND_SUB_PART: {
                            type: ArgumentType.NUMBER,
                            menu: "HAND_SUB_PART",
                            defaultValue: 3,
                        },
                    },
                },
                {
                    opcode: "videoToggle",
                    text: formatMessage({
                        id: "posehand.videoToggle",
                        default: "turn video [VIDEO_STATE]",
                        description:
                            "Control the display of the camera video preview",
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        VIDEO_STATE: {
                            type: ArgumentType.STRING,
                            menu: "VIDEO_STATE",
                            defaultValue: "off",
                        },
                    },
                },
                {
                    opcode: "setVideoTransparency",
                    text: formatMessage({
                        id: "posehand.setVideoTransparency",
                        default: "set video transparency to [TRANSPARENCY]",
                        description:
                            "Adjust the opacity of the camera video preview (0-100)",
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        TRANSPARENCY: {
                            type: ArgumentType.NUMBER,
                            defaultValue: CONFIG.DEFAULT_VIDEO_TRANSPARENCY,
                        },
                    },
                },
            ],
            menus: {
                HAND_PART: {
                    acceptReporters: true,
                    items: this.fingerParts,
                },
                HAND_SUB_PART: {
                    acceptReporters: true,
                    items: this.fingerSubParts,
                },
                VIDEO_STATE: {
                    acceptReporters: true,
                    items: [
                        {
                            text: formatMessage({
                                id: "posehand.videoState.off",
                                default: "off",
                            }),
                            value: VideoState.OFF,
                        },
                        {
                            text: formatMessage({
                                id: "posehand.videoState.on",
                                default: "on",
                            }),
                            value: VideoState.ON,
                        },
                        {
                            text: formatMessage({
                                id: "posehand.videoState.onFlipped",
                                default: "on flipped",
                            }),
                            value: VideoState.ON_FLIPPED,
                        },
                    ],
                },
            },
        };
    }

    /**
     * Main hand detection loop that processes video frames.
     * Runs continuously while the extension is active.
     * @private
     */
    async _loop() {
        while (true) {
            const frame = this.runtime.ioDevices.video.getFrame({
                format: "image-data",
                dimensions: this.DIMENSIONS,
            });

            if (frame) {
                try {
                    this.handPoseState = await this._estimateHandPoseOnImage(
                        frame
                    );
                } catch (err) {
                    // Only log errors that are not about 'util' (preserving existing error filtering)
                    if (!String(err).includes("util")) {
                        console.error("Error detecting hand:", err);
                    }
                }
            }
            await new Promise((r) => setTimeout(r, CONFIG.DETECTION_INTERVAL));
        }
    }

    /**
     * Estimate hand pose on a given image using the HandPose model.
     * @param {ImageData} imageElement - The image data to analyze
     * @returns {Promise<Array>} Array of detected hand poses
     * @private
     */
    async _estimateHandPoseOnImage(imageElement) {
        const handModel = await this._getLoadedHandModel();
        return await handModel.estimateHands(imageElement, {
            flipHorizontal: false,
        });
    }

    /**
     * Get the loaded HandPose model, loading it if necessary.
     * @returns {Promise<object>} The loaded HandPose model
     * @private
     */
    async _getLoadedHandModel() {
        if (this.handModel) return this.handModel;
        if (!window.handpose) {
            await this._loadHandposeScript();
        }
        this.handModel = await window.handpose.load();
        return this.handModel;
    }

    /**
     * Dynamically load TensorFlow.js and HandPose libraries from CDN.
     * This approach allows the extension to work without bundling large ML libraries.
     * @returns {Promise<void>} Promise that resolves when scripts are loaded
     * @private
     */
    _loadHandposeScript() {
        return new Promise((resolve, reject) => {
            // Load TensorFlow.js if not already present
            const loadTf = () => {
                if (window.tf) return Promise.resolve();
                return new Promise((res, rej) => {
                    const tfScript = document.createElement("script");
                    tfScript.src = CONFIG.TENSORFLOW_CDN_URL;
                    tfScript.onload = res;
                    tfScript.onerror = rej;
                    document.head.appendChild(tfScript);
                });
            };

            // Load HandPose model if not already present
            const loadHandpose = () => {
                if (window.handpose) return Promise.resolve();
                return new Promise((res, rej) => {
                    const handposeScript = document.createElement("script");
                    handposeScript.src = CONFIG.HANDPOSE_CDN_URL;
                    handposeScript.onload = res;
                    handposeScript.onerror = rej;
                    document.head.appendChild(handposeScript);
                });
            };

            // Load both scripts in sequence
            loadTf().then(loadHandpose)
                .then(resolve)
                .catch(reject);
        });
    }

    /**
     * Convert TensorFlow coordinates to Scratch coordinates.
     * Preserves the existing coordinate transformation logic.
     * @param {object} coords - The coordinates object with x, y, z properties
     * @param {number} coords.x - X coordinate
     * @param {number} coords.y - Y coordinate
     * @param {number} coords.z - Z coordinate (depth)
     * @returns {object} Converted coordinates {x, y}
     * @private
     */

    /**
     * Check if a hand is currently detected.
     * @returns {boolean} True if hand is detected
     * @private
     */
    isConnected() {
        return !!(this.handPoseState && this.handPoseState.length > 0);
    }

    /**
     * Initialize extension with default settings when project starts.
     * Called automatically by Scratch runtime.
     */
    projectStarted() {
        this.setVideoTransparency({
            TRANSPARENCY: this.globalVideoTransparency,
        });
        this.videoToggle({ VIDEO_STATE: this.globalVideoState });
    }

    /**
     * Move sprite to the detected position of a specific finger part.
     * Block implementation for "go to [HAND_PART] [HAND_SUB_PART]".
     * @param {object} args - Block arguments
     * @param {string} args.HAND_PART - Name of finger to track
     * @param {number} args.HAND_SUB_PART - Index of finger joint (0-3)
     * @param {object} util - Block utility object with target sprite reference
     */
    goToHandPart(args, util) {
        if (!util || !util.target) return;
        if (!this.isConnected()) return;

        const handPart = args.HAND_PART;
        const fingerPart = Cast.toNumber(args.HAND_SUB_PART);

        try {
            // Extract coordinates from hand pose annotations
            const [x, y, z] =
                this.handPoseState[0].annotations[handPart][fingerPart];

            // Get stage dimensions
            const stageWidth = this.runtime.constructor.STAGE_WIDTH;
            const stageHeight = this.runtime.constructor.STAGE_HEIGHT;

            // Convert coordinates from video space (0-480, 0-360) to Scratch space (-240 to 240, -180 to 180)
            const scratchX =
                (x / this.DIMENSIONS[0]) * stageWidth - stageWidth / 2;
            const scratchY =
                (1 - y / this.DIMENSIONS[1]) * stageHeight - stageHeight / 2;

            // Keep sprite within stage bounds
            const fencedPosition = util.target.keepInFence(scratchX, scratchY);

            // Set the position
            util.target.setXY(fencedPosition[0], fencedPosition[1]);
        } catch (e) {
            // If hand or part not detected, do nothing (preserving original silent failure behavior)
        }
    }

    /**
     * Control video preview display state.
     * Block implementation for "turn video [VIDEO_STATE]".
     * @param {object} args - Block arguments
     * @param {string} args.VIDEO_STATE - Desired video state (off/on/on-flipped)
     */
    videoToggle(args) {
        const state = Cast.toString(args.VIDEO_STATE);
        this.globalVideoState = state;

        if (state === VideoState.OFF) {
            this.runtime.ioDevices.video.disableVideo();
        } else {
            this.runtime.ioDevices.video.enableVideo();
            this.runtime.ioDevices.video.mirror = state === VideoState.ON;
        }
    }

    /**
     * Set video preview transparency.
     * Block implementation for "set video transparency to [TRANSPARENCY]".
     * @param {object} args - Block arguments
     * @param {number} args.TRANSPARENCY - Transparency percentage (0-100)
     */
    setVideoTransparency(args) {
        const transparency = Cast.toNumber(args.TRANSPARENCY);

        // Clamp transparency value to valid range
        this.globalVideoTransparency = Math.max(0, Math.min(100, transparency));
        this.runtime.ioDevices.video.setPreviewGhost(
            this.globalVideoTransparency
        );
    }
}

module.exports = Scratch3PoseHandBlocks;
